import { useEffect, useState } from "react";

type Session = "checking" | "signed-in" | "signed-out" | "unavailable";

export function useReportSession(open: boolean) {
  const [session, setSession] = useState<Session>("checking");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!open) return;
    let controller: AbortController | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    function check() {
      controller?.abort();
      clearTimeout(timer);
      const current = new AbortController();
      controller = current;
      if (!navigator.onLine) {
        setSession("unavailable");
        return;
      }
      setSession("checking");
      timer = setTimeout(() => current.abort(), 5000);
      void fetch("/api/session", { signal: current.signal })
        .then(async (response) => {
          const state: Session = response.ok
            ? (await response.json()).user
              ? "signed-in"
              : "signed-out"
            : response.status === 401
              ? "signed-out"
              : "unavailable";
          if (controller === current) setSession(state);
        })
        .catch(() => {
          if (controller === current) setSession("unavailable");
        })
        .finally(() => {
          if (controller === current) clearTimeout(timer);
        });
    }
    check();
    window.addEventListener("online", check);
    window.addEventListener("offline", check);
    return () => {
      controller?.abort();
      controller = undefined;
      clearTimeout(timer);
      window.removeEventListener("online", check);
      window.removeEventListener("offline", check);
    };
  }, [open, revision]);
  return { session, refresh: () => setRevision((value) => value + 1) };
}
